import React, { useState } from 'react';
import { Dropdown, Button, Badge, Spin } from 'antd';
import {
  BellOutlined,
  AppstoreOutlined,
  ShopOutlined,
  StarOutlined,
  UserOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../store/slices/notificationsSlice';
import type { Notification } from '../../types';

const RECENT_LIMIT = 12;

function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function iconForType(type: Notification['type']) {
  const className = 'text-lg leading-none';
  switch (type) {
    case 'lead':
      return <UserOutlined className={className} />;
    case 'review':
      return <StarOutlined className={className} />;
    case 'listing':
      return <AppstoreOutlined className={className} />;
    case 'store':
      return <ShopOutlined className={className} />;
    default:
      return <BellOutlined className={className} />;
  }
}

function iconWrapClass(type: Notification['type']): string {
  switch (type) {
    case 'lead':
      return 'bg-cyan-50 text-cyan-700';
    case 'review':
      return 'bg-amber-50 text-amber-700';
    case 'listing':
      return 'bg-violet-50 text-violet-700';
    case 'store':
      return 'bg-sky-50 text-sky-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function routeForNotification(n: Notification): string {
  switch (n.type) {
    case 'lead':
      return '/leads';
    case 'review':
      return '/reviews';
    case 'listing':
      return '/listings';
    case 'store':
      return '/stores';
    default:
      return '/notifications';
  }
}

const NotificationBellDropdown: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, loading } = useAppSelector((state) => state.notifications);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      dispatch(fetchNotifications({ limit: 30 }));
    }
  };

  const close = () => setOpen(false);

  const recent = [...notifications]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, RECENT_LIMIT);

  const panel = (
    <div
      className="w-[min(calc(100vw-1.5rem),22rem)] overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-2xl sm:w-[26rem]"
      role="dialog"
      aria-label="Notifications"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 bg-gradient-to-b from-slate-50/80 to-white px-4 py-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold leading-snug tracking-tight text-gray-900">Notifications</div>
          {unreadCount > 0 ? (
            <p className="mt-1 text-xs leading-relaxed text-gray-500">{unreadCount} unread</p>
          ) : (
            <p className="mt-1 text-xs leading-relaxed text-gray-500">You&apos;re all caught up</p>
          )}
        </div>
        {unreadCount > 0 ? (
          <Button
            type="link"
            size="small"
            className="!h-auto shrink-0 px-1 py-0 text-xs font-medium text-[#0171BF]"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dispatch(markAllNotificationsAsRead());
            }}
          >
            Mark all read
          </Button>
        ) : null}
      </div>

      <div className="max-h-[min(420px,65vh)] overflow-y-auto overscroll-contain">
        {loading && notifications.length === 0 ? (
          <div className="flex justify-center py-14">
            <Spin />
          </div>
        ) : recent.length === 0 ? (
          <div className="flex flex-col items-center gap-2.5 px-6 py-11 text-center">
            <InboxOutlined className="text-[1.75rem] leading-none text-gray-300" aria-hidden />
            <p className="text-sm font-semibold leading-snug text-gray-800">No notifications yet</p>
            <p className="max-w-[16rem] text-xs leading-relaxed text-gray-500">
              We&apos;ll show new leads, reviews, and updates here.
            </p>
          </div>
        ) : (
          <ul className="m-0 list-none p-0">
            {recent.map((n) => (
              <li key={n.id} className="border-b border-gray-50 last:border-b-0">
                <button
                  type="button"
                  className={[
                    'flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50',
                    !n.read ? 'bg-[#0171BF]/[0.06]' : '',
                  ].join(' ')}
                  onClick={() => {
                    dispatch(markNotificationAsRead(n.id));
                    close();
                    navigate(routeForNotification(n));
                  }}
                >
                  <div
                    className={[
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                      iconWrapClass(n.type),
                    ].join(' ')}
                    aria-hidden
                  >
                    {iconForType(n.type)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-2">
                      <span
                        className={[
                          'line-clamp-2 text-[0.8125rem] leading-snug',
                          n.read ? 'font-medium text-gray-800' : 'font-semibold text-gray-900',
                        ].join(' ')}
                      >
                        {n.title}
                      </span>
                      {!n.read ? (
                        <span
                          className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#0171BF]"
                          title="Unread"
                          aria-label="Unread"
                        />
                      ) : null}
                    </div>
                    {n.message ? (
                      <p className="mt-0.5 line-clamp-2 text-xs leading-relaxed text-gray-600">{n.message}</p>
                    ) : null}
                    <span className="mt-1 block text-[0.6875rem] leading-normal text-gray-400">
                      {formatRelativeTime(n.createdAt)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-gray-100 bg-slate-50/90 px-2 py-1.5">
        <Button
          type="text"
          block
          size="small"
          className="!h-9 text-sm font-medium text-[#0171BF] hover:bg-white"
          onClick={() => {
            close();
            navigate('/notifications');
          }}
        >
          View all notifications
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      trigger={['click']}
      placement="bottomRight"
      destroyOnHidden
      onOpenChange={handleOpenChange}
      popupRender={() => panel}
    >
      <Button
        type="text"
        title={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        className="flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-600 hover:bg-blue-50 hover:text-[#0171BF]"
      >
        <Badge count={unreadCount > 0 ? unreadCount : 0} size="small" overflowCount={99} offset={[-2, 4]}>
          <span className="inline-flex items-center justify-center leading-none">
            <BellOutlined className="text-xl" />
          </span>
        </Badge>
      </Button>
    </Dropdown>
  );
};

export default NotificationBellDropdown;
